/**
 * Country hover highlight system.
 * Renders a semi-transparent GeoJSON polygon over the hovered country
 * and boosts opacity of events within that country.
 */

import { GeoJsonLayer } from '@deck.gl/layers';
import type { Layer } from '@deck.gl/core';
import type { Feature } from 'geojson';
import { eventBus } from '@/core/events';
import { getCountryGeoJson, type RawCountryFeature } from '@/map/country-detect';

/** Currently hovered country code, or null. */
let hoveredCountryCode: string | null = null;
let hoveredFeature: RawCountryFeature | null = null;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
const DEBOUNCE_MS = 100;

/** Callback to trigger a layer rebuild when hover state changes. */
let onHighlightChange: (() => void) | null = null;

/**
 * Initialize the country highlight system.
 * Listens to country:hover events and triggers layer rebuilds.
 */
export function initCountryHighlight(rebuildCallback: () => void): () => void {
  onHighlightChange = rebuildCallback;

  const unsub = eventBus.on('country:hover', (country) => {
    // Debounce to avoid flicker during rapid mouse movement
    if (debounceTimer !== null) {
      clearTimeout(debounceTimer);
    }

    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      const newCode = country?.code ?? null;

      if (newCode === hoveredCountryCode) return;

      hoveredCountryCode = newCode;

      if (newCode) {
        hoveredFeature = getCountryGeoJson(newCode);
      } else {
        hoveredFeature = null;
      }

      if (onHighlightChange) {
        onHighlightChange();
      }
    }, DEBOUNCE_MS);
  });

  return unsub;
}

/**
 * Build the country highlight deck.gl layer.
 * Returns an empty array if no country is hovered.
 */
export function buildCountryHighlightLayer(): Layer[] {
  if (!hoveredFeature || !hoveredCountryCode) return [];

  const featureCollection = {
    type: 'FeatureCollection' as const,
    features: [hoveredFeature as unknown as Feature],
  };

  return [
    new GeoJsonLayer<Feature>({
      id: 'country-highlight',
      data: featureCollection,
      pickable: false,
      stroked: true,
      filled: true,
      getFillColor: [255, 255, 255, 20],   // semi-transparent white fill
      getLineColor: [255, 255, 255, 90],  // visible white border
      getLineWidth: 1.5,
      lineWidthUnits: 'pixels' as const,
    }),
  ];
}

/**
 * Get the currently hovered country code.
 * Used by the layer orchestrator to boost opacity of matching events.
 */
export function getHoveredCountryCode(): string | null {
  return hoveredCountryCode;
}

/** Clean up the country highlight system. */
export function destroyCountryHighlight(): void {
  hoveredCountryCode = null;
  hoveredFeature = null;
  onHighlightChange = null;
  if (debounceTimer !== null) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
}
